import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Matcher } from 'react-day-picker';

import { formatDate, getDate } from '@/helpers';
import { mergeClasses } from '@/utils';

import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import {
  Button,
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';

import { DEFAULT_MAX_YEAR, DEFAULT_MIN_YEAR } from './DatePicker.constants';

interface IDatePickerRef {
  onClear: () => void;
}

interface IDatePicker {
  /** Click handler for applying the updates from DatePicker. */
  onUpdate?: (values: { date: Date | undefined }) => void;
  /** Initial value for date */
  initialDate?: Date | string;
  /** Alignment of popover */
  align?: 'start' | 'center' | 'end';
  /** Option for locale */
  locale?: string;
  /** Minimum year for the calendar */
  minYear?: number;
  /** Maximum year for the calendar */
  maxYear?: number;
  /** Additional class name for the date picker */
  className?: string;
  /** Label for the date picker */
  label?: string;
  /** Is the date picker required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Is the date picker disabled */
  disabled?: boolean;
  /** Check if suffix icon should be displayed */
  hasSuffixIcon?: boolean;
  /** Additional class name for the trigger button */
  buttonClassName?: string;
  /** Placeholder text for the date picker */
  placeholder?: string;
  /** Allow clearing the selected date */
  allowClear?: boolean;
  /** Disable specific dates in the calendar */
  calendarDisabled?: Matcher | Matcher[] | undefined;
  /** Render the popover as modal */
  isModal?: boolean;
}

/** The DatePicker component allows a user to select a single date */
const DatePicker = forwardRef<IDatePickerRef, IDatePicker>(
  (
    {
      initialDate,
      onUpdate,
      align = 'end',
      locale = 'en-GB',
      minYear = DEFAULT_MIN_YEAR,
      maxYear = DEFAULT_MAX_YEAR,
      className,
      label,
      required,
      error,
      disabled,
      hasSuffixIcon,
      buttonClassName,
      placeholder,
      allowClear,
      calendarDisabled,
      isModal = false,
    }: IDatePicker,
    ref,
  ) => {
    const [isOpen, setOpen] = useState(false);

    const [date, setDate] = useState<Date | undefined>(getDate(initialDate));

    // To prevent onUpdate from firing on initial render
    const isInitialRender = useRef(true);

    useEffect(() => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      onUpdate?.({ date });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const onClear = (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      setDate(undefined);
      onUpdate?.({ date: undefined });
    };

    useImperativeHandle(ref, () => ({
      onClear: onClear,
    }));

    return (
      <div className={mergeClasses('flex flex-col gap-2', className)}>
        {label && (
          <Label className="!gap-0 font-semibold text-body-sm text-neutral-9">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </Label>
        )}
        <Popover modal={isModal} open={isOpen} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              buttonStyle="outline"
              buttonColor="neutral"
              borderRadius="soft"
              className={mergeClasses(
                'focus:shadow-none border-neutral-4 dark:bg-neutral-10 dark:border-neutral-9 !justify-start',
                // Disable state
                disabled && '[&_*]:text-neutral-7',
                // Data state: open
                'data-[state=open]:border-primary-base',
                error && 'border-semantic-error-base',
                buttonClassName,
              )}
              prefixIcon={
                <CalendarIcon
                  className={mergeClasses(
                    'size-5 text-primary-base',
                    disabled && '!text-neutral-6',
                  )}
                />
              }
              suffixIcon={
                <div className="flex items-center gap-1">
                  {allowClear && date && (
                    <CloseIcon
                      className="!size-5 cursor-pointer text-neutral-6 dark:text-neutral-6 hover:text-neutral-10 dark:hover:text-neutral-5"
                      onClick={() => onClear()}
                    />
                  )}
                  {hasSuffixIcon && (
                    <SvgIcon
                      className={mergeClasses(
                        'size-5 cursor-pointer text-neutral-10 dark:!text-primary-base',
                        isOpen && '!text-primary-base',
                      )}
                      component={isOpen ? ChevronUpIcon : ChevronDownIcon}
                    />
                  )}
                </div>
              }
              disabled={disabled}
            >
              <div
                className={mergeClasses(
                  'font-medium text-body-sm w-full flex-1 text-left',
                  !date ? 'text-neutral-7' : 'text-neutral-10 dark:text-neutral-0',
                )}
              >
                {placeholder && !date
                  ? placeholder
                  : formatDate({
                      date,
                      defaultText: 'Select Date',
                      locale,
                      yearAfterComma: false,
                    })}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align={align}
            side="bottom"
            avoidCollisions={false}
            className="p-none w-auto bg-neutral-0"
          >
            <Calendar
              mode="single"
              onSelect={(value: Date | undefined) => {
                setDate(value);
                setOpen(false);
              }}
              selected={date}
              numberOfMonths={1}
              defaultMonth={date ?? new Date()}
              startMonth={new Date(minYear, 0)}
              endMonth={new Date(maxYear, 11)}
              captionLayout="dropdown"
              footer
              disabled={calendarDisabled}
            />
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';

export { DatePicker, type IDatePicker };
